import { css } from "@emotion/css";
import { space } from "@octopusdeploy/design-system-tokens";
import type { EnvironmentV2Resource, ProjectResource, TenantResource } from "@octopusdeploy/octopus-server-client";
import { sortBy } from "lodash";
import React, { useMemo } from "react";
import { SourceCardList } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/components/SourceCardList";
import { Select } from "~/components/form/index";
import type { Item } from "~/primitiveComponents/form/Select/Select";
import { sortDropDownItems } from "~/primitiveComponents/form/Select/Select";
import type { ConfiguredSource, ConfiguredSources } from "../ConfiguredSource";
import { sharedStyles } from "../sharedStyles";
import { AnnotationHelpNote } from "./AnnotationHelpNote";
const styles = {
    annotationHelp: css({
        marginBottom: space[16],
    }),
};
type ScopingConfigurationViewProps = {
    index: number;
    configuredSources: ConfiguredSources;
    setConfiguredSources: (configuredSources: ConfiguredSources) => void;
    projects: ProjectResource[] | undefined;
    environments: EnvironmentV2Resource[] | undefined;
    tenants: TenantResource[] | undefined;
    isMultiSourceApplication: boolean;
};
export const ScopingConfigurationView = (props: ScopingConfigurationViewProps) => {
    const { index, configuredSources, setConfiguredSources, projects, environments, tenants, isMultiSourceApplication } = props;
    //We sort the project items here so when we retrieve the first one to set a default value, we get the same first item as in the select
    //Otherwise you get the first created project, which might be sorted into a different order in the drop down
    const projectItems: Item[] = useMemo(() => sortDropDownItems((projects || []).map((p) => ({
        value: p.Slug,
        text: p.Name,
    }))), [projects]);
    const environmentItems: Item[] = useMemo(() => sortBy(environments || [], (e) => e.SortOrder).map((e) => ({
        value: e.Slug,
        text: e.Name,
    })), [environments]);
    const tenantItems: Item[] = useMemo(() => (tenants || []).map((t) => ({
        value: t.Slug,
        text: t.Name,
    })), [tenants]);
    const updateConfiguredSource = (key: string, updated: Partial<ConfiguredSource>) => {
        const updatedSources = { ...configuredSources };
        updatedSources[key] = {
            ...configuredSources[key],
            ...updated,
        };
        setConfiguredSources(updatedSources);
    };
    const selectedSources = useMemo(() => {
        const selectedEntries = Object.entries(configuredSources).filter(([, configuredSource]) => configuredSource.isSelectedForScoping);
        return Object.fromEntries(selectedEntries);
    }, [configuredSources]);
    return (<div>
            <h2 className={sharedStyles.header}>{index}. Configure scoping annotations</h2>
            <div className={styles.annotationHelp}>
                <p>These annotations map your Argo CD application to Octopus projects, environments and tenants.</p>
                <AnnotationHelpNote />
            </div>
            <SourceCardList configuredSources={selectedSources} isMultiSourceApplication={isMultiSourceApplication} cardContentRenderer={(key, configuredSource) => (<>
                        <Select label={`Project`} allowClear={false} items={projectItems} value={configuredSource.projectSlug} onChange={(val) => updateConfiguredSource(key, { projectSlug: val })} disabled={projectItems.length === 0} allowFilter={true} sortItems={false}/>
                        <Select label={`Environment`} allowClear={false} items={environmentItems} value={configuredSource.environmentSlug} onChange={(val) => updateConfiguredSource(key, { environmentSlug: val })} disabled={environmentItems.length === 0} allowFilter={true} sortItems={false}/>
                        <Select label={"Tenant (optional)"} allowClear={true} items={tenantItems} value={configuredSource.tenantSlug} onChange={(val) => updateConfiguredSource(key, { tenantSlug: val })} disabled={tenantItems.length === 0} allowFilter={true}/>
                    </>)}/>
        </div>);
};
